// Catalog.ID Client — Authenticator Key on screen
// ============================================================================
// registration-restructure §15.9 (decided 2026-09-09): the `otpauth://` QR is
// shown on SCREEN wherever the key is enrolled — the registration step (the
// ceremony's secret is still in memory), the Unlock Sheet flow's outcome screen
// (the node returned the reissued secret once) and Settings' *Turn on* / *Show
// key* / *Issue a new key*. Sheet-only was weighed and rejected: a member whose
// kit is in a bank safe must be able to turn the code on, or set up a
// replacement phone, without a trip.
//
// One panel for all of them: the QR, the 32 characters in groups of four (for
// an app that cannot scan), and — where the caller asks for it — a button that
// prints the SAME single sheet the kit carries, through the kit pipeline with
// the letterhead.

import { createElement } from '@catalog-shell';
import { formatAuthenticatorKey, otpauthUri } from '../crypto/totp';
import { qrToSvg } from './qr-svg';
import { buildAuthenticatorKeyPage } from './booklet/authenticator-key-page';
import { buildBookletDefinition } from './booklet/booklet';
import { formatVersion, type AccountHeaderData } from './booklet/shared';
import { printPdf } from './pdf-loader';

/** Printed title of the single-sheet print — the Unlock Sheet reissue's own
 *  pattern (`renderResetCardGroup`), so a sheet printed here is
 *  indistinguishable from the one in the kit. */
export const AUTHENTICATOR_SHEET_TITLE = 'Catalog.ID Authenticator Key';

export interface AuthenticatorKeyPanelOptions {
    /** The account the key belongs to — the QR's label and the sheet's header. */
    cid: string;
    secret: Uint8Array;
    /** The URI as the NODE built it, when the node supplied one (reveal, or the
     *  Unlock Sheet completion). Absent ⇒ derived locally from the secret; both
     *  are pinned to the same contract. */
    uri?: string;
    /** One line above the QR. */
    intro?: string;
    /** Account-header rows for the printed sheet: the account-type label and,
     *  for a delegate, Organization / Domain / Role. The cid and the version
     *  stamp are filled in here. */
    header?: Partial<Omit<AccountHeaderData, 'cid' | 'version'>>;
    /** Offer "Print Authenticator Key sheet". Default true. */
    print?: boolean;
}

/**
 * Append the panel to `host`. Returns the panel element so a caller can hide or
 * replace it.
 *
 * The print button opens a window, so it must run from the click itself —
 * `printPdf`'s popup-activation rule (§11). Nothing here awaits before it.
 */
export function appendAuthenticatorKeyPanel(
    host: HTMLElement, options: AuthenticatorKeyPanelOptions,
): HTMLElement {
    const panel = createElement('div', { className: 'cid-auth-field' });

    if (options.intro) {
        panel.appendChild(createElement('p', { className: 'cid-text-muted', textContent: options.intro }));
    }

    const uri = options.uri ?? otpauthUri(options.cid, options.secret);
    const qr = createElement('div', { style: { textAlign: 'center', margin: '8px 0' } });
    qr.innerHTML = qrToSvg(new TextEncoder().encode(uri));
    const svg = qr.querySelector('svg');
    if (svg) {
        svg.setAttribute('width', '200');
        svg.setAttribute('height', '200');
    }
    panel.appendChild(qr);

    const keyRow = createElement('p', {
        className: 'cid-credential-value',
        textContent: formatAuthenticatorKey(options.secret),
        style: { textAlign: 'center', letterSpacing: '0.05em' },
    });
    panel.appendChild(keyRow);
    panel.appendChild(createElement('p', {
        className: 'cid-text-muted',
        textContent: 'Type these characters instead if your app cannot scan.',
    }));

    if (options.print !== false) {
        const printError = createElement('div', { className: 'cid-pane-error' });
        const printBtn = createElement('button', {
            className: 'cs-btn cs-btn--default',
            textContent: 'Print Authenticator Key sheet',
        });
        printBtn.addEventListener('click', () => {
            printError.classList.remove('cid-pane-error--visible');
            try {
                // Synchronous up to printPdf so the popup keeps its activation.
                const pages = [buildAuthenticatorKeyPage({
                    header: {
                        accountTypeLabel: 'Personal',
                        ...options.header,
                        cid: options.cid,
                        version: formatVersion(new Date()),
                    },
                    secret: options.secret,
                })];
                void printPdf(buildBookletDefinition(pages, [], { title: AUTHENTICATOR_SHEET_TITLE }))
                    .catch((err: unknown) => {
                        printError.textContent = err instanceof Error ? err.message : 'Print failed';
                        printError.classList.add('cid-pane-error--visible');
                    });
            } catch (err) {
                printError.textContent = err instanceof Error ? err.message : 'Print failed';
                printError.classList.add('cid-pane-error--visible');
            }
        });
        panel.appendChild(printBtn);
        panel.appendChild(printError);
    }

    host.appendChild(panel);
    return panel;
}
